import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { MdArrowBack } from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import * as apicall from '../../components/Apicall';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FiMinusCircle } from "react-icons/fi";
import { useFormikContext } from 'formik';


const DoctorAvailability = ({ showBackButton = true, showPadding = true }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname.toLowerCase();
      if (!currentPath.includes('/users/add') && !currentPath.includes('/users/profile/')) {
        sessionStorage.removeItem('doctorDetails');
        sessionStorage.removeItem('doctorAvailability');

      }
    };
  }, []);

  // Get doctor details from session storage
  const doctorDetails = JSON.parse(sessionStorage.getItem('doctorDetails') || '{}');


  const defaultStartTime = doctorDetails.working_hours_start || '';
  const defaultEndTime = doctorDetails.working_hours_end || '';
  const defaultFeeFrom = doctorDetails.fee_from || '';
  const defaultFeeTo = doctorDetails.fee_to || '';

  const savedAvailability = sessionStorage.getItem('doctorAvailability');
  const initialValues = savedAvailability
    ? JSON.parse(savedAvailability)
    : {
        appointmentDuration: '15',
        numberOfSlots: '1',
        schedule: [
          {
            day: 1,
            checked: true,
            workingHours: [{ startTime: defaultStartTime, endTime: defaultEndTime, fee: '' }],
            breakTimings: [{ breakStartTime: '', breakEndTime: '' }]
          },
          {
            day: 2,
            checked: true,
            workingHours: [{ startTime: defaultStartTime, endTime: defaultEndTime, fee: '' }],
            breakTimings: [{ breakStartTime: '', breakEndTime: '' }]
          },
          {
            day: 3,
            checked: true,
            workingHours: [{ startTime: defaultStartTime, endTime: defaultEndTime, fee: '' }],
            breakTimings: [{ breakStartTime: '', breakEndTime: '' }]
          },
          {
            day: 4,
            checked: true,
            workingHours: [{ startTime: defaultStartTime, endTime: defaultEndTime, fee: '' }],
            breakTimings: [{ breakStartTime: '', breakEndTime: '' }]
          },
          {
            day: 5,
            checked: true,
            workingHours: [{ startTime: defaultStartTime, endTime: defaultEndTime, fee: '' }],
            breakTimings: [{ breakStartTime: '', breakEndTime: '' }]
          },
          {
            day: 6,
            checked: false,
            workingHours: [{ startTime: '', endTime: '', fee: '' }],
            breakTimings: [{ breakStartTime: '', breakEndTime: '' }]
          },
          {
            day: 7,
            checked: false,
            workingHours: [{ startTime: '', endTime: '', fee: '' }],
            breakTimings: [{ breakStartTime: '', breakEndTime: '' }]
          },
        ]
      };

  const validationSchema = Yup.object().shape({
    appointmentDuration: Yup.string().required('Appointment duration is required'),
    numberOfSlots: Yup.string().required('Number of slots is required'),
    schedule: Yup.array().of(
      Yup.object().shape({
        checked: Yup.boolean(),
        workingHours: Yup.array().of(
          Yup.object().shape({
            startTime: Yup.string()
              .test('required-if-checked', 'Start time is required when day is selected', function (value) {
                return !this.options.from[1].value.checked || (this.options.from[1].value.checked && value);
              })
              .test('start-time-within-range', 'Start time must be within vendors working hours range', function (value) {
                if (!value || !this.options.from[1].value.checked) return true;

                if (!defaultStartTime || !defaultEndTime) return true;

                return value >= defaultStartTime && value <= defaultEndTime;
              })
              .test('start-time-after-previous-end', 'Start time must be after previous row end time', function (value) {
                if (!value || !this.options.from[1].value.checked) return true;

                const workingHours = this.from[1].value.workingHours;
                const currentIndex = workingHours.findIndex(
                  wh => wh.startTime === value
                );

                if (currentIndex === 0) return true;

                const previousEndTime = workingHours[currentIndex - 1].endTime;
                if (!previousEndTime) return true;

                return value > previousEndTime;
              }),
            endTime: Yup.string()
              .test('required-if-checked', 'End time is required when day is selected', function (value) {
                return !this.options.from[1].value.checked || (this.options.from[1].value.checked && value);
              })
              .test('end-time-within-range', 'End time must be within vendors working hours range', function (value) {
                if (!value || !this.options.from[1].value.checked) return true;

                if (!defaultStartTime || !defaultEndTime) return true;

                return value >= defaultStartTime && value <= defaultEndTime;
              })
              .test('end-time-after-start', 'End time must be after start time', function (value) {
                if (!this.parent.startTime || !value) return true;
                return value > this.parent.startTime;
              }),
            fee: Yup.string()
              .test('required-if-checked', 'Fee is required when day is selected', function (value) {
                return !this.options.from[1].value.checked || (this.options.from[1].value.checked && value);
              })
              .test('fee-within-range', 'Fee must be within the allowed range', function (value) {
                if (!value || !this.options.from[1].value.checked) return true;

                const fee = parseInt(value);
                const from = parseInt(defaultFeeFrom) || 0;
                const to = parseInt(defaultFeeTo) || Infinity;

                if (isNaN(fee)) return false;
                return fee >= from && fee <= to;
              })
              .test('fee-is-number', 'Fee must be a valid number', function (value) {
                if (!value) return true;
                return !isNaN(parseInt(value));
              })
              .test('fee-is-positive', 'Fee must be a positive number', function (value) {
                if (!value) return true;
                return parseInt(value) > 0;
              })
          })
        ),
        breakTimings: Yup.array().of(
          Yup.object().shape({
            breakStartTime: Yup.string()
              .test('break-start-validation', 'Break start time must be within valid working hours', function (value) {
                if (!value || !this.options.from[1].value.checked) return true;

                const day = this.options.from[1].value;
                const workingHours = day.workingHours;

                // First try to validate against current row's working hours
                const currentRowValidHours = workingHours.filter(wh =>
                  wh.startTime && wh.endTime && wh.startTime !== '00:00' && wh.endTime !== '00:00'
                );

                if (currentRowValidHours.length > 0) {
                  return currentRowValidHours.some(wh =>
                    value >= wh.startTime && value <= wh.endTime
                  );
                }

                // If no valid working hours in current row, try to get from first row
                const formValues = this.options.parent;
                if (formValues && formValues.schedule) {
                  const firstDayWithHours = formValues.schedule.find(day =>
                    day.workingHours.some(wh =>
                      wh.startTime && wh.endTime && wh.startTime !== '00:00' && wh.endTime !== '00:00'
                    )
                  );

                  if (firstDayWithHours) {
                    const firstValidHours = firstDayWithHours.workingHours.find(wh =>
                      wh.startTime && wh.endTime && wh.startTime !== '00:00' && wh.endTime !== '00:00'
                    );

                    if (firstValidHours) {
                      return value >= firstValidHours.startTime && value <= firstValidHours.endTime;
                    }
                  }
                }

                // If no reference found, skip validation
                return true;
              }),
            breakEndTime: Yup.string()
            .when('breakStartTime', {
                is: (val) => !!val, 
                then: (schema) => schema.required('Break end time is required if break start time is entered'),
                otherwise: (schema) => schema
              })
              .test('break-end-after-start', 'Break end time must be after start time', function (value) {
                if (!this.parent.breakStartTime || !value) return true;
                return value > this.parent.breakStartTime;
              })
              .test('break-end-validation', 'Break end time must be within valid working hours', function (value) {
                if (!value || !this.options.from[1].value.checked) return true;

                const day = this.options.from[1].value;
                const workingHours = day.workingHours;

                // First try to validate against current row's working hours
                const currentRowValidHours = workingHours.filter(wh =>
                  wh.startTime && wh.endTime && wh.startTime !== '00:00' && wh.endTime !== '00:00'
                );

                if (currentRowValidHours.length > 0) {
                  return currentRowValidHours.some(wh =>
                    value >= wh.startTime && value <= wh.endTime
                  );
                }

                // If no valid working hours in current row, try to get from first row
                const formValues = this.options.parent;
                if (formValues && formValues.schedule) {
                  const firstDayWithHours = formValues.schedule.find(day =>
                    day.workingHours.some(wh =>
                      wh.startTime && wh.endTime && wh.startTime !== '00:00' && wh.endTime !== '00:00'
                    )
                  );

                  if (firstDayWithHours) {
                    const firstValidHours = firstDayWithHours.workingHours.find(wh =>
                      wh.startTime && wh.endTime && wh.startTime !== '00:00' && wh.endTime !== '00:00'
                    );

                    if (firstValidHours) {
                      return value >= firstValidHours.startTime && value <= firstValidHours.endTime;
                    }
                  }
                }

                // If no reference found, skip validation
                return true;
              })
              .test('break-time-overlap', 'Break time cannot overlap with another break', function (value) {
                if (!value || !this.parent.breakStartTime) return true;

                const breakTimings = this.options.from[1].value.breakTimings;
                if (!breakTimings || breakTimings.length <= 1) return true;

                const currentBreakIndex = breakTimings.findIndex(
                  bt => bt.breakStartTime === this.parent.breakStartTime && bt.breakEndTime === value
                );

                return !breakTimings.some((bt, index) => {
                  if (index === currentBreakIndex || !bt.breakStartTime || !bt.breakEndTime) return false;

                  return (
                    (this.parent.breakStartTime >= bt.breakStartTime && this.parent.breakStartTime < bt.breakEndTime) ||
                    (value > bt.breakStartTime && value <= bt.breakEndTime) ||
                    (this.parent.breakStartTime <= bt.breakStartTime && value >= bt.breakEndTime)
                  );
                });
              })
          })
        )
      })
    ),
  });

  // Format dates properly
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };
  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    const doctorDetails = JSON.parse(sessionStorage.getItem('doctorDetails'));
    if (!doctorDetails) {
      toast.error('Doctor details not found. Please go back and try again.');
      return;
    }

    setLoading(true);
    try {
      const shifts = values.schedule
        .filter(day => day.checked)
        .flatMap(day => {
          const shiftsForDay = [];

          // Process all working hours for this day
          day.workingHours.forEach((workingHour, whIndex) => {
            if (!workingHour.startTime || !workingHour.endTime) {
              return; // Skip incomplete working hours
            }

            // Create the main shift with working hours
            const mainShift = {
              day: day.day,
              start_time: `${workingHour.startTime}:00`,
              end_time: `${workingHour.endTime}:00`,
              fee: parseInt(workingHour.fee) || parseInt(doctorDetails.consultationFees || 0)
            };

            // Filter break timings that fall within this specific working hour
            const validBreakTimings = day.breakTimings.filter(breakTiming =>
              breakTiming.breakStartTime &&
              breakTiming.breakEndTime &&
              breakTiming.breakStartTime >= workingHour.startTime &&
              breakTiming.breakStartTime <= workingHour.endTime &&
              breakTiming.breakEndTime >= workingHour.startTime &&
              breakTiming.breakEndTime <= workingHour.endTime
            );

            if (validBreakTimings.length === 0) {
              // No break timings for this working hour, just add the main shift
              shiftsForDay.push(mainShift);
            } else {
              // Add the main shift with the first break timing
              shiftsForDay.push({
                ...mainShift,
                break_start_time: `${validBreakTimings[0].breakStartTime}:00`,
                break_end_time: `${validBreakTimings[0].breakEndTime}:00`
              });

              // Add additional shifts for remaining break timings (with empty working hours)
              for (let i = 1; i < validBreakTimings.length; i++) {
                shiftsForDay.push({
                  day: day.day,
                  start_time: "00:00:00",
                  end_time: "00:00:00",
                  fee: parseInt(workingHour.fee) || parseInt(doctorDetails.consultationFees || 0),
                  break_start_time: `${validBreakTimings[i].breakStartTime}:00`,
                  break_end_time: `${validBreakTimings[i].breakEndTime}:00`
                });
              }
            }
          });

          return shiftsForDay;
        });

      // Prepare name parts
      const nameParts = (doctorDetails.doctorName || '').trim().split(' ');
      const roleValue = doctorDetails.role || '';

      // Prepare the complete data payload
      const data = {
        doctorName: doctorDetails.doctorName,
        first_name: nameParts[0] || '',
        middle_name: nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '',
        last_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
        email: doctorDetails.email,
        phone_number: doctorDetails.phone_number,
        gender: parseInt(doctorDetails.gender),
        education: doctorDetails.education,
        specilisation: parseInt(doctorDetails.specialization),
        vendor_id: doctorDetails.vendor_id,
        country: doctorDetails.country || 1,
        state: doctorDetails.state || 1,
        about_user: doctorDetails.about_user,
        city: doctorDetails.city || 1,
        address: doctorDetails.address || '',
        unique_id: doctorDetails.unique_id || '',
        is_active: 1,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        dob: formatDate(doctorDetails.dob),
        created_by: 300,
        is_login_eligible: false,
        profile_image: doctorDetails.profile_image || '',
        fee: parseInt(doctorDetails.fee) || 0,
        practice_start: formatDate(doctorDetails.practice_start) || '0000-00-00',
        role: parseInt(roleValue),
        appointment_duration: parseInt(values.appointmentDuration),
        no_of_slots: parseInt(values.numberOfSlots),
        shifts: shifts
      };

      // Save to sessionStorage
      sessionStorage.setItem('doctorAvailability', JSON.stringify(values));

      // Make API call
      const res = await apicall.post(data, 'service_providers_register');
      if (res && (res.status === 200 || (res.message?.includes('successfully')))) {
        const successMessage = res.message || 'User added successfully';

        toast.success(successMessage, {
          onClose: () => {
            sessionStorage.removeItem('doctorDetails');
            sessionStorage.removeItem('doctorAvailability');
            navigate('/users');
          },
          autoClose: 100,
          closeButton: false,
          pauseOnHover: false
        });
      } else {
        throw new Error(res?.message || 'Failed to add doctor');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error(error.message.replace('Error:', ''));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const SaveAvailabilityToSession = () => {
    const { values } = useFormikContext();
    useEffect(() => {
      sessionStorage.setItem('doctorAvailability', JSON.stringify(values));
    }, [values]);
    return null;
  };

  return (
    <div className={showPadding ? "px-0" : ""}>
      {showBackButton && (
        <div className="d-flex align-items-center mb-3 pt-3">
          <Button
            variant="link"
            className="text-dark text-decoration-none p-0 d-flex align-items-center"
            onClick={() => navigate('/users/add')}
            style={{ fontSize: '14px' }}
          >
            <MdArrowBack size={20} /> BACK
          </Button>
        </div>
      )}

      <div className="bg-white rounded p-4">
        <h5 className="mb-2" style={{ fontSize: '16px', fontWeight: '600', color: '#212529' }}>
          Set your availability
        </h5>
        <p className="mb-4" style={{ fontSize: '14px', color: '#6C757D' }}>
          Customize your availability or use the default setting of Monday - Friday, {defaultStartTime || ''} - {defaultEndTime || ''} by selecting below
        </p>
        <div className="divide-border mb-4 mt-3" />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting
          }) => (
            <>
              <SaveAvailabilityToSession />
              <Form onSubmit={handleSubmit}>
                <Row className="mb-4">
                  <Col md={3}>
                    <Form.Group style={{ maxWidth: '200px' }}>
                      <Form.Label >
                        Appointment Duration
                      </Form.Label>
                      <Form.Select
                        name="appointmentDuration"
                        value={values.appointmentDuration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.appointmentDuration && errors.appointmentDuration}
                        className='form-text'
                      >
                        <option value="15">15 min</option>
                        <option value="30">30 min</option>
                        <option value="45">45 min</option>
                        <option value="60">60 min</option>
                      </Form.Select>
                      {touched.appointmentDuration && errors.appointmentDuration && (
                        <Form.Control.Feedback type="invalid">{errors.appointmentDuration}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group style={{ maxWidth: '200px' }}>
                      <Form.Label >
                        Number of slots
                      </Form.Label>
                      <Form.Select
                        name="numberOfSlots"
                        value={values.numberOfSlots}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.numberOfSlots && errors.numberOfSlots}
                        className='form-text'

                      >
                        {[...Array(10)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        ))}
                      </Form.Select>
                      {touched.numberOfSlots && errors.numberOfSlots && (
                        <Form.Control.Feedback type="invalid">{errors.numberOfSlots}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col className="d-flex justify-content-end align-items-end me-5">
                    <div
                      onClick={() => {
                        // Find the first checked day
                        const firstCheckedIndex = values.schedule.findIndex(day => day.checked);
                        if (firstCheckedIndex === -1) return; // No checked day, do nothing

                        values.schedule.forEach((_, idx) => {
                          if (idx !== firstCheckedIndex) {
                            setFieldValue(
                              `schedule.${idx}.workingHours`,
                              values.schedule[firstCheckedIndex].workingHours.map(wh => ({ ...wh }))
                            );
                            setFieldValue(
                              `schedule.${idx}.breakTimings`,
                              values.schedule[firstCheckedIndex].breakTimings.map(bt => ({ ...bt }))
                            );
                            setFieldValue(`schedule.${idx}.checked`, values.schedule[firstCheckedIndex].checked);
                          }
                        });
                      }}
                      className="apply-to-all custom-btn btn-larges">
                      Apply to All Weeks
                    </div>
                  </Col>
                </Row>

                <div className="divide-border mb-4 mt-n2" />
                <div className="mb-3">
                  <Row className="mb-2 align-items-center" style={{ fontWeight: 600, fontSize: 14 }}>
                    <Col md={2}>Days</Col>
                    <Col md={4}>
                      Working Hours
                      {defaultStartTime && defaultEndTime && (
                        <span className="text-muted" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                          ( {defaultStartTime} - {defaultEndTime})
                        </span>
                      )}
                    </Col>
                    <Col md={2} style={{ fontSize: '14px' }}>
                      Fee
                      {defaultFeeFrom && defaultFeeTo && (
                        <span className="text-muted" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                          (Range: {defaultFeeFrom} - {defaultFeeTo} AED)
                        </span>
                      )}
                    </Col>
                    <Col md={4}>Break Timing <span style={{ color: '#6C757D' }}>(Optional)</span></Col>
                  </Row>

                  {values.schedule.map((day, dayIndex) => (
                    <React.Fragment key={day.day}>
                      <Row className="mb-3">
                        <Col md={2}>
                          <Form.Check
                            type="checkbox"
                            id={`schedule.${dayIndex}.checked`}
                            name={`schedule.${dayIndex}.checked`}
                            label={['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex]}
                            checked={values.schedule[dayIndex].checked}
                            onChange={(e) => {
                              setFieldValue(`schedule.${dayIndex}.checked`, e.target.checked);
                              if (!e.target.checked) {
                                setFieldValue(`schedule.${dayIndex}.workingHours`, [{ startTime: '', endTime: '', fee: '' }]);
                                setFieldValue(`schedule.${dayIndex}.breakTimings`, [{ breakStartTime: '', breakEndTime: '' }]);
                              }
                            }}
                            style={{ fontSize: '14px' }}
                            className="custom-checkbox"
                          />
                        </Col>
                        <Col md={4}>
                          {day.workingHours.map((wh, whIdx) => (
                            <Row key={whIdx} className="align-items-center mb-2">
                              <Col xs={5}>
                                <Form.Control
                                  type="time"
                                  name={`schedule.${dayIndex}.workingHours.${whIdx}.startTime`}
                                  value={wh.startTime}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={!day.checked}
                                  isInvalid={
                                    touched.schedule?.[dayIndex]?.workingHours?.[whIdx]?.startTime &&
                                    errors.schedule?.[dayIndex]?.workingHours?.[whIdx]?.startTime
                                  }
                                  className='form-text'
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.schedule?.[dayIndex]?.workingHours?.[whIdx]?.startTime}
                                </Form.Control.Feedback>
                              </Col>
                              <Col xs={5}>
                                <Form.Control
                                  type="time"
                                  name={`schedule.${dayIndex}.workingHours.${whIdx}.endTime`}
                                  value={wh.endTime}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={!day.checked}
                                  isInvalid={
                                    touched.schedule?.[dayIndex]?.workingHours?.[whIdx]?.endTime &&
                                    errors.schedule?.[dayIndex]?.workingHours?.[whIdx]?.endTime
                                  }
                                  className='form-text'
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.schedule?.[dayIndex]?.workingHours?.[whIdx]?.endTime}
                                </Form.Control.Feedback>
                              </Col>
                              <Col xs={2} className="d-flex justify-content-center">
                                {day.workingHours.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newWH = day.workingHours.filter((_, idx) => idx !== whIdx);
                                      setFieldValue(`schedule.${dayIndex}.workingHours`, newWH);
                                    }}
                                    className="btn p-0 border-0 bg-transparent d-flex align-items-center justify-content-center"

                                    aria-label="Remove row"
                                  >
                                    <FiMinusCircle size={24} />
                                  </button>
                                )}
                              </Col>
                            </Row>
                          ))}
                          {day.checked && (
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => {
                                setFieldValue(`schedule.${dayIndex}.workingHours`, [
                                  ...day.workingHours,
                                  { startTime: '', endTime: '', fee: '' }
                                ]);
                              }}
                              className="add-row-btn">
                              Add Row
                            </Button>
                          )}

                        </Col>
                        <Col md={2}>
                          {day.workingHours.map((wh, whIdx) => (
                            <Row key={whIdx} className="align-items-center mb-2">
                              <Col xs={12}>
                                <Form.Control
                                  type="number"
                                  name={`schedule.${dayIndex}.workingHours.${whIdx}.fee`}
                                  value={wh.fee}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder="Fee"
                                  disabled={!day.checked}
                                  isInvalid={
                                    touched.schedule?.[dayIndex]?.workingHours?.[whIdx]?.fee &&
                                    errors.schedule?.[dayIndex]?.workingHours?.[whIdx]?.fee
                                  }
                                  className='form-text'
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.schedule?.[dayIndex]?.workingHours?.[whIdx]?.fee}
                                </Form.Control.Feedback>
                              </Col>
                            </Row>
                          ))}
                        </Col>
                        <Col md={4}>
                          {day.breakTimings.map((br, brIdx) => (
                            <Row key={brIdx} className="align-items-center mb-2">
                              <Col xs={5}>
                                <Form.Control
                                  type="time"
                                  name={`schedule.${dayIndex}.breakTimings.${brIdx}.breakStartTime`}
                                  value={br.breakStartTime}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={!day.checked}
                                  isInvalid={
                                    touched.schedule?.[dayIndex]?.breakTimings?.[brIdx]?.breakStartTime &&
                                    errors.schedule?.[dayIndex]?.breakTimings?.[brIdx]?.breakStartTime
                                  }
                                  className='form-text'
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.schedule?.[dayIndex]?.breakTimings?.[brIdx]?.breakStartTime}
                                </Form.Control.Feedback>
                              </Col>
                              <Col xs={5}>
                                <Form.Control
                                  type="time"
                                  name={`schedule.${dayIndex}.breakTimings.${brIdx}.breakEndTime`}
                                  value={br.breakEndTime}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={!day.checked}
                                  isInvalid={
                                    touched.schedule?.[dayIndex]?.breakTimings?.[brIdx]?.breakEndTime &&
                                    errors.schedule?.[dayIndex]?.breakTimings?.[brIdx]?.breakEndTime
                                  }
                                  className='form-text'
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.schedule?.[dayIndex]?.breakTimings?.[brIdx]?.breakEndTime}
                                </Form.Control.Feedback>
                              </Col>
                              <Col xs={2} className="d-flex justify-content-center">
                                {day.breakTimings.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newBreaks = day.breakTimings.filter((_, idx) => idx !== brIdx);
                                      setFieldValue(`schedule.${dayIndex}.breakTimings`, newBreaks);
                                    }}
                                    className="btn p-0 border-0 bg-transparent d-flex align-items-center justify-content-center"

                                    aria-label="Remove break"
                                  >
                                    <FiMinusCircle size={24} />
                                  </button>
                                )}
                              </Col>
                            </Row>
                          ))}
                          {day.checked && (
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => {
                                setFieldValue(`schedule.${dayIndex}.breakTimings`, [
                                  ...day.breakTimings,
                                  { breakStartTime: '', breakEndTime: '' }
                                ]);
                              }}
                              className="add-row-btn">
                              Add Row
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </React.Fragment>
                  ))}
                </div>

                <div className="d-flex bg-white mt-2 p-2 justify-content-start gap-4">
                  <Button
                    variant="light"
                    onClick={() => navigate('/users/add')}
                    className="custom-btn btn-large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="danger"
                    disabled={isSubmitting || loading}
                    className="custom-btn btn-large"
                  >
                    {loading ? 'Loading...' : 'Submit'}
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default DoctorAvailability;